import { cn } from "@-/com.npmjs.cn";
import { View } from "@-/com.npmjs.react-native";
import {
  LongPressable,
  useSelectableTextPress,
} from "@-/dev.improve.haniverse.components.ui.long-pressable";
import {
  Surface,
  SurfaceContent,
  SurfaceHeader,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { copyToClipboard } from "@-/dev.improve.haniverse.lib.clipboard";
import type { ReferenceGraphemeMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { morphemeSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useAppSelection } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";
import {
  graphemeFontWeightStyle,
  useFontStyleForGrapheme,
  useGraphemeDisplayText,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.font";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { useCardFrequencyRank } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.rank";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import { useMemo } from "react";
import { GraphemeDictionaryReadingRow } from "#grapheme-dictionary-reading-row";
import { useGraphemeDictionaryReadings } from "#use-grapheme-dictionary-readings";

/**
 * The character's surface in the dictionary sheet, laid out like the word surfaces beneath it: the
 * glyph (tap to copy) with its definitions and frequency rank in the header, then one expandable
 * row per pronunciation holding that reading's definitions and curriculum grade.
 */
export function GraphemeDictionarySheetSurface({
  className,
  graphemeCard,
}: {
  className?: string;
  graphemeCard: ReferenceGraphemeMorphemeSlice;
}) {
  const glyph = useGetGraphemeGlyph()(graphemeCard.grapheme);
  const displayText = useGraphemeDisplayText(glyph.display);
  const fontStyle = useFontStyleForGrapheme(glyph.display);
  const rank = useCardFrequencyRank(graphemeCard);
  const { definitions, readings } = useGraphemeDictionaryReadings(graphemeCard);
  const selectionItems = useMemo(
    () => [morphemeSelectionItem(graphemeCard.grapheme)],
    [graphemeCard.grapheme],
  );
  const { consumePress, isSelected, onLongPress } = useAppSelection(selectionItems);
  const [isDefinitionsExpanded, setIsDefinitionsExpanded] = useRouteBodyState(
    "grapheme-surface-definitions-expanded",
    false,
  );
  const joinedDefinitions = definitions.join("; ");
  // Holding the definitions selects their text natively; that hold's release must not toggle them.
  const expandedDefinitionsPress = useSelectableTextPress(() => setIsDefinitionsExpanded(false));
  const collapsedDefinitionsPress = useSelectableTextPress(() => setIsDefinitionsExpanded(true));

  return (
    <Surface className={className} testID="grapheme-dictionary-surface">
      <SurfaceHeader className="px-0" testID="grapheme-dictionary-surface-header">
        <View className="flex-row items-center gap-x-2">
          <LongPressable
            accessibilityLabel={`Copy ${glyph.display}`}
            accessibilityState={{ selected: isSelected }}
            className={cn(
              "items-center justify-center py-2 pr-1 pl-3 active:bg-accent",
              isSelected && "bg-primary/10",
            )}
            onLongPress={onLongPress}
            onPress={() => {
              if (!consumePress()) {
                void copyToClipboard(glyph.display, { label: glyph.display });
              }
            }}
          >
            {/* As tall as a word's glyph and pinyin, so the headers share one rhythm. */}
            <Text
              className="text-center text-[40px] leading-12 font-medium text-foreground"
              style={[
                fontStyle,
                graphemeFontWeightStyle(glyph.fontWeight),
                glyph.color === null ? undefined : { color: glyph.color },
              ]}
              variant="glyph"
            >
              {displayText}
            </Text>
          </LongPressable>
          <View className="min-w-0 flex-1 py-2">
            {hasLength(definitions, 0) ? (
              <Text className="text-sm text-muted-foreground italic">
                No dictionary definitions found yet.
              </Text>
            ) : isDefinitionsExpanded ? (
              <Text
                accessibilityLabel={`Show fewer definitions for ${graphemeCard.grapheme}`}
                accessibilityRole="button"
                accessibilityState={{ expanded: true }}
                className="text-sm leading-5 text-muted-foreground"
                {...expandedDefinitionsPress}
                selectable
              >
                {definitions.map((definition) => `• ${definition}`).join("\n")}
              </Text>
            ) : (
              <Text
                accessibilityLabel={`Show all definitions for ${graphemeCard.grapheme}`}
                accessibilityRole="button"
                accessibilityState={{ expanded: false }}
                className="text-sm leading-5 text-muted-foreground"
                numberOfLines={2}
                {...collapsedDefinitionsPress}
                selectable
              >
                {joinedDefinitions}
              </Text>
            )}
          </View>
          {/* A character's frequency doesn't depend on its reading; grades sit on each reading. */}
          <View className="items-end self-start py-2 pr-3">
            <Text className="text-right text-xs text-muted-foreground">
              {rank === null ? "No Rank" : `#${rank}`}
            </Text>
          </View>
        </View>
      </SurfaceHeader>
      {hasLength(readings, 0) ? null : (
        <SurfaceContent testID="grapheme-dictionary-surface-body">
          {readings.map((reading, index) => (
            <GraphemeDictionaryReadingRow
              grapheme={graphemeCard.grapheme}
              key={reading.toneNumbersPinyin}
              reading={reading}
              showTopDivider={index > 0}
            />
          ))}
        </SurfaceContent>
      )}
    </Surface>
  );
}
